import { analyzeCheckCommand, parseTestCounts } from '@viko/protocol';
export function emptyProgress() {
    return {
        goal: '',
        startedAt: 0,
        busy: false,
        checks: [],
        commits: [],
        milestones: [],
        artifacts: [],
        calls: {},
        unverifiedOk: {},
        asks: [],
        errors: 0,
        edits: {},
        stallMs: 600000,
    };
}
/** Pure incremental evidence reducer. Prose and todos never advance verified progress. */
export function reduceProgress(p, e) {
    let n = p;
    const patch = (x) => {
        n = { ...n, ...x };
    };
    const addCheck = (c) => {
        const previous = n.checks.filter((x) => x.command === c.command).at(-1);
        const existing = n.checks.find((x) => x.id === c.id);
        const fewerFailures = c.kind === 'test' &&
            c.status === 'fail' &&
            c.exitCode != null &&
            c.total != null &&
            c.total === previous?.total &&
            c.failed != null &&
            previous?.failed != null &&
            c.failed < previous.failed;
        const advance = fewerFailures ||
            (c.status === 'pass' &&
                (c.failed ?? 0) === 0 &&
                c.exitCode === 0 &&
                (!previous ||
                    previous.status !== 'pass' ||
                    (c.passed ?? 0) > (previous.passed ?? 0) ||
                    (c.headSha && c.headSha !== previous.headSha)));
        patch({
            checks: [
                ...n.checks.filter((x) => x.id !== c.id),
                { ...c, seq: existing?.seq ?? e.seq, ...(e.turnId ? { turnId: e.turnId } : {}) },
            ].slice(-300),
            ...(advance ? { lastAdvance: e.ts, errors: 0, edits: {} } : {}),
        });
    };
    switch (e.kind) {
        case 'run.start':
            patch({ startedAt: e.ts });
            break;
        case 'user.msg':
            if (!p.goal)
                patch({ goal: e.text });
            break;
        case 'turn.start':
            patch({ busy: true, startedAt: p.startedAt || e.ts });
            break;
        case 'turn.end':
            patch({ busy: false });
            break;
        case 'status':
            patch({
                busy: e.state === 'running' || e.state === 'compacting',
                ...(e.state === 'running' ? { environment: undefined } : {}),
            });
            break;
        case 'todo':
            patch({ milestones: e.items });
            break;
        case 'question':
            patch({ asks: [...p.asks, e.questionId] });
            break;
        case 'approval':
            patch({ asks: [...p.asks, e.approvalId] });
            break;
        case 'question.answered':
            patch({ asks: p.asks.filter((x) => x !== e.questionId) });
            break;
        case 'approval.resolved':
            patch({ asks: p.asks.filter((x) => x !== e.approvalId) });
            break;
        case 'notice':
            if (e.code?.startsWith('stall:'))
                patch({ environment: e.text });
            break;
        case 'usage':
            // A subagent's context window is not the session's.
            if (e.contextPct != null && !e.parentId)
                patch({ contextPct: e.contextPct });
            break;
        case 'ratelimit':
            patch({
                rateLimitUntil: Math.max(0, ...e.windows
                    .filter((w) => w.status === 'rejected' || (w.usedPct ?? 0) >= 100)
                    .map((w) => w.resetsAt ?? e.ts + 60000)),
            });
            break;
        case 'error':
            patch({ errors: p.errors + 1 });
            break;
        case 'artifact':
            patch({
                artifacts: [
                    ...p.artifacts.filter((a) => a.id !== e.artifactId),
                    { id: e.artifactId, seq: e.seq },
                ],
            });
            break;
        case 'tool.call': {
            if (e.toolKind === 'shell' && e.command)
                patch({
                    calls: {
                        ...p.calls,
                        [e.toolCallId]: {
                            command: e.command,
                            ts: e.ts,
                            ...(e.turnId ? { turnId: e.turnId } : {}),
                        },
                    },
                });
            break;
        }
        case 'tool.result': {
            // Diff hunks are actual completed edit results; identical regions are only a loop signal.
            for (const d of e.status === 'ok' ? (e.diffs ?? []) : [])
                for (const h of d.hunks ?? []) {
                    const key = `${d.path}:${h.oldStart}`;
                    patch({ edits: { ...n.edits, [key]: (n.edits[key] ?? 0) + 1 } });
                }
            const call = p.calls[e.toolCallId];
            if (call) {
                const calls = { ...n.calls };
                delete calls[e.toolCallId];
                patch({ calls });
                const kind = analyzeCheckCommand(call.command, p.recipe);
                if (kind && e.status !== 'denied' && e.status !== 'interrupted') {
                    const counts = kind.kind === 'test' ? parseTestCounts(e.output) : {};
                    const base = {
                        id: e.toolCallId,
                        command: call.command,
                        kind: kind.kind,
                        source: 'agent',
                        ts: e.ts,
                        durationMs: e.durationMs ?? e.ts - call.ts,
                        ...counts,
                    };
                    if (kind.exit === 'direct' && e.exitCode != null)
                        addCheck({
                            ...base,
                            status: e.exitCode === 0 && e.status === 'ok' ? 'pass' : 'fail',
                            exitCode: e.exitCode,
                        });
                    else {
                        // Observed, not verified: a piped check reports its filter's exit status and a
                        // missing exit code is unknown. Status reflects the parsed output only.
                        addCheck({
                            ...base,
                            exitUnknown: kind.exit === 'piped' ? 'piped' : 'unreported',
                            status: (counts.failed ?? 0) > 0 || (counts.passed == null && e.status === 'error')
                                ? 'fail'
                                : 'pass',
                        });
                        // Not a verified pass, but the failure streak is no longer observed as unbroken.
                        if (e.status === 'ok' && !(counts.failed ?? 0))
                            patch({ unverifiedOk: { ...n.unverifiedOk, [call.command]: e.seq } });
                    }
                }
            }
            // Error loops are consecutive failures; any completed tool call breaks the chain.
            if (e.status === 'error')
                patch({ errors: n.errors + 1 });
            else if (e.status === 'ok')
                patch({ errors: 0 });
            break;
        }
        case 'progress': {
            patch({
                observedAt: e.ts,
                ...(e.recipe ? { recipe: e.recipe } : {}),
                ...(e.stallAfterMs ? { stallMs: e.stallAfterMs } : {}),
            });
            if (e.git) {
                const d = e.git.dirty;
                const was = p.git?.dirty;
                if (was
                    ? d.files !== was.files ||
                        d.additions !== was.additions ||
                        d.deletions !== was.deletions
                    : d.files > 0)
                    patch({ dirtyAt: e.ts });
                const fresh = e.git.commits.filter((c) => !p.commits.some((x) => x.sha === c.sha));
                patch({
                    git: e.git,
                    gitAt: e.ts,
                    commits: [...p.commits, ...fresh.map((c) => ({ sha: c.sha, seq: e.seq }))],
                    ...(fresh.length ? { lastAdvance: e.ts, errors: 0, edits: {} } : {}),
                });
            }
            for (const c of e.checks)
                addCheck(c);
            if (e.landed)
                patch({ landed: e.landed, lastAdvance: e.ts });
            break;
        }
    }
    return n;
}
export const PROGRESS_LABEL = {
    unknown: 'Awaiting evidence',
    working: 'Working · nothing verified yet',
    'making-progress': 'Making progress',
    stalled: 'Stalled',
    looping: 'Looping',
    'blocked-user': 'Blocked · waiting on you',
    'blocked-environment': 'Blocked · environment',
    ready: 'Ready to land',
    landed: 'Landed',
};
export function progressModel(p, { now, cursor = 0, offline = false, lastActivity, busy: busyOverride, }) {
    const busy = busyOverride ?? p.busy;
    // Only results whose exit code is the check's own are verification.
    const latest = [...new Map(p.checks.filter(isVerified).map((c) => [c.command, c])).values()];
    const observedTest = p.checks.findLast((c) => c.kind === 'test');
    const signals = [];
    for (const c of latest) {
        const history = p.checks.filter((x) => x.command === c.command && isVerified(x));
        let failures = 0;
        const brokenAt = p.unverifiedOk[c.command] ?? -1;
        for (const x of history.reverse()) {
            if (x.seq < brokenAt)
                break;
            if (x.status !== 'fail' && x.status !== 'error')
                break;
            if (x.failed !== c.failed ||
                JSON.stringify([...(x.failing ?? [])].sort()) !==
                    JSON.stringify([...(c.failing ?? [])].sort()))
                break;
            failures++;
        }
        if (failures >= 3)
            signals.push(`${c.command} failed ${failures} consecutive times${c.failing?.length ? `, same failures: ${c.failing.slice(0, 2).join(', ')}` : ''}`);
    }
    for (const [region, count] of Object.entries(p.edits))
        if (count >= 3)
            signals.push(`${region} edited ${count} times without verified advancement`);
    if (p.errors >= 3)
        signals.push(`${p.errors} consecutive errors`);
    const looping = signals.length > 0;
    if ((p.contextPct ?? 0) >= 90)
        signals.push(`Context ${p.contextPct}% full`);
    if ((p.rateLimitUntil ?? 0) > now)
        signals.push('Rate limited; waiting for reset');
    const recipe = [...(p.recipe?.test ?? []), ...(p.recipe?.build ?? [])];
    const recipeGreen = p.git?.dirty.files === 0 &&
        recipe.length > 0 &&
        recipe.every((cmd) => latest.some((c) => c.command === cmd &&
            c.status === 'pass' &&
            (c.failed ?? 0) === 0 &&
            c.exitCode === 0 &&
            c.headSha === p.git?.headSha &&
            c.cleanTree === true));
    // A cached remote ref or merge-tree success alone is never landing readiness.
    const ready = !!(recipeGreen &&
        p.git?.baseVerifiedAt &&
        now - p.git.baseVerifiedAt < 120000 &&
        p.git.ahead > 0 &&
        p.git.behind === 0 &&
        p.git.dirty.files === 0);
    let state = 'unknown';
    let reason = 'No verified advancement yet. Agent statements are claims.';
    if (p.lastAdvance) {
        state = 'making-progress';
        const test = latest.findLast((c) => c.kind === 'test');
        reason = [
            p.git
                ? `${p.git.ahead} commits ahead · ${p.git.diffstat.files} files +${p.git.diffstat.additions}/−${p.git.diffstat.deletions}.`
                : 'A check result improved.',
            test
                ? `Last test: ${test.passed ?? 'unknown'} passed · ${test.failed ?? 'unknown'} failed.`
                : 'No test counts observed yet.',
        ].join(' ');
    }
    if (busy) {
        // Stalled means silence: no agent events, tool output or worktree changes. Activity without
        // verified advancement stays neutral; it is never a warning and never progress.
        const activeAt = Math.max(p.startedAt, p.lastAdvance ?? 0, p.dirtyAt ?? 0, lastActivity ?? 0);
        const unverifiedFor = now - (p.lastAdvance ?? p.startedAt);
        if (now - activeAt >= p.stallMs) {
            state = 'stalled';
            reason = `No activity for ${Math.floor((now - activeAt) / 60000)} min.`;
        }
        else if (!p.lastAdvance || unverifiedFor >= p.stallMs) {
            state = 'working';
            reason = p.lastAdvance
                ? `Active, but nothing verified for ${Math.floor(unverifiedFor / 60000)} min.`
                : 'Active. No verified advancement yet; agent statements are claims.';
        }
    }
    if (looping) {
        state = 'looping';
        reason = signals[0] ?? '';
    }
    if (ready) {
        state = 'ready';
        reason = 'Fresh base, no rebase needed, clean tree and recipe verified at this HEAD.';
    }
    if (p.environment || (p.rateLimitUntil ?? 0) > now) {
        state = 'blocked-environment';
        reason = p.environment ?? 'The provider reported a rate limit.';
    }
    if (p.asks.length) {
        state = 'blocked-user';
        reason = `${p.asks.length} unanswered question or approval request.`;
    }
    if (p.landed) {
        state = 'landed';
        reason = `Landing pipeline verified ${p.landed.sha?.slice(0, 8) ?? 'the merge'}.`;
    }
    if (offline) {
        state = 'unknown';
        reason = 'Disconnected. Evidence below is the last observed snapshot.';
    }
    return {
        state,
        label: PROGRESS_LABEL[state],
        reason,
        signals,
        latest,
        /** Newest test run, verified or not (its `exitUnknown` says which). */
        observedTest,
        recipeGreen,
        delta: {
            checks: p.checks.filter((c) => c.seq > cursor).length,
            commits: p.commits.filter((c) => c.seq > cursor).length,
            artifacts: p.artifacts.filter((a) => a.seq > cursor).length,
        },
    };
}
/** A check result whose exit code is the check's own (not piped, not missing). */
export function isVerified(c) {
    return c.exitCode != null && !c.exitUnknown;
}
/** "exit status unknown (piped)" / "exit status not reported" for observed-only results. */
export function exitNote(c) {
    if (c.exitUnknown === 'piped')
        return 'exit status unknown (piped)';
    if (c.exitUnknown === 'unreported' || c.exitCode == null)
        return 'exit status not reported';
    return `exit ${c.exitCode}`;
}
//# sourceMappingURL=progress.js.map